import { toast } from "sonner"

import { parseFrame, splitFrames } from "@/lib/sse"
import { getActiveWorkspaceId, setActiveWorkspaceId } from "@/lib/workspace"
import type {
  AdminConfig,
  AdminDebtRow,
  AdminExpenseRow,
  AdminHealth,
  AdminRecentRun,
  AdminRefreshToolsResult,
  AdminSpend,
  AdminStt,
  AdminStats,
  AdminSyncPluginsResult,
  AdminUserRow,
  AdminWorkspaceRow,
  Agent,
  AgentPlugin,
  AuditEntry,
  AuthConfig,
  BillingPlans,
  Connection,
  Conversation,
  DiscoveryResult,
  MailAccount,
  MailPreset,
  McpTool,
  Memory,
  Message,
  MessageResponse,
  NotionConnection,
  Page,
  Plugin,
  PluginOAuthStart,
  Provider,
  QuotaStatus,
  Run,
  RunDetail,
  RunEvent,
  RunTrace,
  Schedule,
  SchedulePreview,
  TelegramBot,
  TelegramChatRef,
  TelegramConnection,
  TelegramLoginStart,
  TelegramLoginStatus,
  TelegramPanelState,
  TokenResponse,
  User,
  Workspace,
  WorkspaceInvite,
  WorkspaceInviteCreated,
  InvitePreview,
  WorkspaceMember,
} from "@/lib/types"

const TOKEN_KEY = "ai-agents.token"

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message)
    this.name = "ApiError"
  }
}

// --- "contact admin" plumbing ---------------------------------------------
// These helpers run outside React (toasts, error boundary), so the current admin
// contact and translator are registered here by the providers as they mount.

let adminContact: string | null = null
type Tr = (key: string, fallback: string) => string
let translate: Tr = (_key, fallback) => fallback

/** Registered by AuthProvider once the public /auth/config has loaded. */
export function setAdminContact(value: string | null): void {
  adminContact = value
}

/** Registered by LocaleProvider so non-React helpers can localize action labels. */
export function setToastTranslator(fn: Tr): void {
  translate = fn
}

/** Turn a configured contact ("@handle", "https://…", "t.me/…") into an openable URL. */
export function adminContactUrl(): string | null {
  const raw = adminContact?.trim()
  if (!raw) return null
  if (/^https?:\/\//i.test(raw)) return raw
  if (raw.startsWith("@")) return `https://t.me/${raw.slice(1)}`
  if (/^[a-zA-Z0-9_]{3,32}$/.test(raw)) return `https://t.me/${raw}`
  return null // free-form (e.g. a phone): send them to /billing where it is shown
}

/** A sonner action that reaches the admin — a direct link when we can build one,
 *  otherwise the billing page, which always shows the contact and payment details. */
export function contactAdminAction(): { label: string; onClick: () => void } {
  const url = adminContactUrl()
  return {
    label: translate("common.contactAdmin", "Contact admin"),
    onClick: () =>
      url ? window.open(url, "_blank", "noopener") : window.location.assign("/billing"),
  }
}

/** Toast an error; quota (402, "credit exhausted") and every other error offer a
 *  "contact admin" action so a stuck user always has a way forward. */
export function toastApiError(error: unknown, fallback: string): void {
  const message = error instanceof ApiError ? error.message : fallback
  toast.error(message, { action: contactAdminAction() })
}

/** Toast a runtime failure (e.g. a failed agent run) with a "contact admin" action. */
export function toastErrorWithContact(message: string): void {
  toast.error(message, { action: contactAdminAction() })
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* private browsing, or storage disabled */
  }
}

function authHeaders(): Record<string, string> {
  const token = getToken()
  const workspaceId = getActiveWorkspaceId()
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(workspaceId ? { "X-Workspace-Id": workspaceId } : {}),
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api/v1${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
      ...(init.headers ?? {}),
    },
  })

  if (!response.ok) {
    // The backend always returns {error: {code, message, details}}; fall back for
    // anything else (a proxy error page, say).
    let code = "http_error"
    let message = `Request failed with status ${response.status}`
    let details: unknown
    try {
      const body = await response.json()
      if (body?.error) {
        code = body.error.code ?? code
        message = body.error.message ?? message
        details = body.error.details
      }
    } catch {
      /* not JSON */
    }
    throw new ApiError(response.status, code, message, details)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const api = {
  /* auth */
  register: (email: string, password: string, displayName?: string) =>
    request<TokenResponse>("/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password, display_name: displayName || null }),
    }),
  login: (email: string, password: string) =>
    request<TokenResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  googleLogin: (credential: string) =>
    request<TokenResponse>("/auth/google", {
      method: "POST",
      body: JSON.stringify({ credential }),
    }),
  authConfig: () => request<AuthConfig>("/auth/config"),
  me: () => request<User>("/auth/me"),
  updateMe: (payload: { timezone: string }) =>
    request<User>("/auth/me", { method: "PATCH", body: JSON.stringify(payload) }),
  registerStart: (email: string, password: string, displayName?: string) =>
    request<MessageResponse>("/auth/register/start", {
      method: "POST",
      body: JSON.stringify({ email, password, display_name: displayName || null }),
    }),
  registerVerify: (email: string, code: string) =>
    request<TokenResponse>("/auth/register/verify", {
      method: "POST",
      body: JSON.stringify({ email, code }),
    }),
  passwordResetRequest: (email: string) =>
    request<MessageResponse>("/auth/password-reset/request", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  passwordResetConfirm: (email: string, code: string, newPassword: string) =>
    request<TokenResponse>("/auth/password-reset/confirm", {
      method: "POST",
      body: JSON.stringify({ email, code, new_password: newPassword }),
    }),

  /* admin */
  adminStats: () => request<AdminStats>("/admin/stats"),
  adminRecentRuns: (limit = 50) =>
    request<AdminRecentRun[]>(`/admin/runs/recent?limit=${limit}`),
  adminSpend: (days = 30) => request<AdminSpend>(`/admin/spend?days=${days}`),
  adminStt: (days = 30) => request<AdminStt>(`/admin/stt?days=${days}`),
  adminExpenses: (limit = 200, offset = 0, category?: string) => {
    const q = new URLSearchParams({ limit: String(limit), offset: String(offset) })
    if (category) q.set("category", category)
    return request<Page<AdminExpenseRow>>(`/admin/expenses?${q.toString()}`)
  },
  adminDebts: (limit = 200, offset = 0, direction?: string, settled?: boolean) => {
    const q = new URLSearchParams({ limit: String(limit), offset: String(offset) })
    if (direction) q.set("direction", direction)
    if (settled !== undefined) q.set("settled", String(settled))
    return request<Page<AdminDebtRow>>(`/admin/debts?${q.toString()}`)
  },
  adminUsers: (limit = 50, offset = 0) =>
    request<Page<AdminUserRow>>(`/admin/users?limit=${limit}&offset=${offset}`),
  adminSetUserActive: (id: string, active: boolean) =>
    request<{ ok: boolean }>(`/admin/users/${id}/${active ? "activate" : "deactivate"}`, {
      method: "POST",
    }),
  adminAudit: (params: { limit?: number; offset?: number; action?: string; actorId?: string } = {}) => {
    const q = new URLSearchParams()
    q.set("limit", String(params.limit ?? 50))
    q.set("offset", String(params.offset ?? 0))
    if (params.action) q.set("action", params.action)
    if (params.actorId) q.set("actor_id", params.actorId)
    return request<Page<AuditEntry>>(`/admin/audit?${q.toString()}`)
  },
  // Billing lives on the workspace (Task 12/13), not the user - the grant
  // targets a workspace id. adminUserWorkspaces lists the target user's
  // workspaces so the caller can pick which one to grant.
  adminGrantPlan: (workspaceId: string, planKey: string, days = 30) =>
    request<{ ok: boolean }>(`/admin/workspaces/${workspaceId}/grant-plan`, {
      method: "POST",
      body: JSON.stringify({ plan_key: planKey, days }),
    }),
  adminTopUp: (workspaceId: string, amountMicroUsd: number) =>
    request<{ ok: boolean }>(`/admin/workspaces/${workspaceId}/top-up`, {
      method: "POST",
      body: JSON.stringify({ amount_micro_usd: amountMicroUsd }),
    }),
  adminUserWorkspaces: (id: string) => request<AdminWorkspaceRow[]>(`/admin/users/${id}/workspaces`),
  adminUserAgents: (id: string) => request<Agent[]>(`/admin/users/${id}/agents`),
  adminUserConnections: (id: string) => request<Connection[]>(`/admin/users/${id}/mcp-connections`),
  adminUserSchedules: (id: string) => request<Schedule[]>(`/admin/users/${id}/schedules`),
  // Admin CRUD on another user's agents. The target user owns the result; the admin
  // is the acting party, so the model picker is honoured (same as an admin's own agent).
  adminCreateUserAgent: (userId: string, payload: Record<string, unknown>) =>
    request<Agent>(`/admin/users/${userId}/agents`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  adminUpdateUserAgent: (userId: string, agentId: string, payload: Record<string, unknown>) =>
    request<Agent>(`/admin/users/${userId}/agents/${agentId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  adminDeleteUserAgent: (userId: string, agentId: string) =>
    request<{ ok: boolean }>(`/admin/users/${userId}/agents/${agentId}`, { method: "DELETE" }),
  // Admin CRUD on another user's MCP connections, mirroring the self-service endpoints.
  adminCreateUserConnection: (userId: string, payload: Record<string, unknown>) =>
    request<DiscoveryResult>(`/admin/users/${userId}/mcp-connections`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  adminUpdateUserConnection: (
    userId: string,
    connectionId: string,
    payload: Record<string, unknown>,
  ) =>
    request<Connection>(`/admin/users/${userId}/mcp-connections/${connectionId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  adminDeleteUserConnection: (userId: string, connectionId: string) =>
    request<{ ok: boolean }>(`/admin/users/${userId}/mcp-connections/${connectionId}`, {
      method: "DELETE",
    }),
  adminUserConnectionTools: (userId: string, connectionId: string) =>
    request<McpTool[]>(`/admin/users/${userId}/mcp-connections/${connectionId}/tools`),
  adminRefreshUserConnection: (userId: string, connectionId: string) =>
    request<DiscoveryResult>(`/admin/users/${userId}/mcp-connections/${connectionId}/refresh`, {
      method: "POST",
    }),
  adminTestUserConnection: (userId: string, connectionId: string) =>
    request<Connection>(`/admin/users/${userId}/mcp-connections/${connectionId}/test`, {
      method: "POST",
    }),
  adminConfig: () => request<AdminConfig>("/admin/config"),
  adminHealth: () => request<AdminHealth>("/admin/health"),
  // Admin direct management of any workspace's members (no invite round-trip).
  adminWorkspaceMembers: (workspaceId: string) =>
    request<WorkspaceMember[]>(`/admin/workspaces/${workspaceId}/members`),
  adminAddWorkspaceMember: (
    workspaceId: string,
    payload: { email?: string; user_id?: string; role: "owner" | "admin" | "member" },
  ) =>
    request<WorkspaceMember>(`/admin/workspaces/${workspaceId}/members`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  adminUpdateWorkspaceMemberRole: (
    workspaceId: string,
    userId: string,
    role: "owner" | "admin" | "member",
  ) =>
    request<WorkspaceMember>(`/admin/workspaces/${workspaceId}/members/${userId}`, {
      method: "PATCH",
      body: JSON.stringify({ role }),
    }),
  adminRemoveWorkspaceMember: (workspaceId: string, userId: string) =>
    request<{ ok: boolean }>(`/admin/workspaces/${workspaceId}/members/${userId}`, {
      method: "DELETE",
    }),
  adminRefreshTools: () =>
    request<AdminRefreshToolsResult>("/admin/ops/refresh-tools", { method: "POST" }),
  adminSyncPlugins: () =>
    request<AdminSyncPluginsResult>("/admin/ops/sync-plugins", { method: "POST" }),

  /* workspaces */
  listWorkspaces: () => request<Workspace[]>("/workspaces"),
  createWorkspace: (name: string) =>
    request<Workspace>("/workspaces", { method: "POST", body: JSON.stringify({ name }) }),
  getWorkspace: (id: string) => request<Workspace>(`/workspaces/${id}`),
  updateWorkspace: (id: string, name: string) =>
    request<Workspace>(`/workspaces/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),
  deleteWorkspace: (id: string) =>
    request<{ ok: boolean }>(`/workspaces/${id}`, { method: "DELETE" }),
  listMembers: (workspaceId: string) =>
    request<WorkspaceMember[]>(`/workspaces/${workspaceId}/members`),
  updateMemberRole: (workspaceId: string, userId: string, role: string) =>
    request<WorkspaceMember>(`/workspaces/${workspaceId}/members/${userId}`, {
      method: "PATCH",
      body: JSON.stringify({ role }),
    }),
  removeMember: (workspaceId: string, userId: string) =>
    request<{ ok: boolean }>(`/workspaces/${workspaceId}/members/${userId}`, {
      method: "DELETE",
    }),
  listInvites: (workspaceId: string) =>
    request<WorkspaceInvite[]>(`/workspaces/${workspaceId}/invites`),
  createInvite: (workspaceId: string, email: string, role: "admin" | "member" = "member") =>
    request<WorkspaceInviteCreated>(`/workspaces/${workspaceId}/invites`, {
      method: "POST",
      body: JSON.stringify({ email, role }),
    }),
  revokeInvite: (workspaceId: string, inviteId: string) =>
    request<{ ok: boolean }>(`/workspaces/${workspaceId}/invites/${inviteId}`, {
      method: "DELETE",
    }),
  resendInvite: (workspaceId: string, inviteId: string) =>
    request<WorkspaceInviteCreated>(`/workspaces/${workspaceId}/invites/${inviteId}/resend`, {
      method: "POST",
    }),
  acceptInvite: (token: string) =>
    request<Workspace>("/invites/accept", { method: "POST", body: JSON.stringify({ token }) }),
  invitePreview: (token: string) =>
    request<InvitePreview>("/invites/preview", { method: "POST", body: JSON.stringify({ token }) }),

  /* audit */
  auditMe: (limit = 50, offset = 0) =>
    request<Page<AuditEntry>>(`/audit/me?limit=${limit}&offset=${offset}`),

  /* billing */
  billingPlans: () => request<BillingPlans>("/billing/plans"),
  billingMe: () => request<QuotaStatus>("/billing/me"),

  /* providers */
  providers: () => request<Provider[]>("/providers"),

  /* connections */
  listConnections: () => request<Connection[]>("/mcp-connections"),
  getConnection: (id: string) => request<Connection>(`/mcp-connections/${id}`),
  createConnection: (payload: Record<string, unknown>) =>
    request<DiscoveryResult>("/mcp-connections", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateConnection: (id: string, payload: Record<string, unknown>) =>
    request<Connection>(`/mcp-connections/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  deleteConnection: (id: string) =>
    request<{ ok: boolean }>(`/mcp-connections/${id}`, { method: "DELETE" }),
  connectionTools: (id: string) => request<McpTool[]>(`/mcp-connections/${id}/tools`),
  refreshConnection: (id: string) =>
    request<DiscoveryResult>(`/mcp-connections/${id}/refresh`, { method: "POST" }),
  testConnection: (id: string) =>
    request<Connection>(`/mcp-connections/${id}/test`, { method: "POST" }),

  /* agents */
  listAgents: () => request<Agent[]>("/agents"),
  getAgent: (id: string) => request<Agent>(`/agents/${id}`),
  createAgent: (payload: Record<string, unknown>) =>
    request<Agent>("/agents", { method: "POST", body: JSON.stringify(payload) }),
  updateAgent: (id: string, payload: Record<string, unknown>) =>
    request<Agent>(`/agents/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteAgent: (id: string) => request<{ ok: boolean }>(`/agents/${id}`, { method: "DELETE" }),

  /* telegram */
  getAgentTelegram: (agentId: string) =>
    request<TelegramBot | null>(`/agents/${agentId}/telegram`),
  saveAgentTelegram: (agentId: string, payload: Record<string, unknown>) =>
    request<TelegramBot>(`/agents/${agentId}/telegram`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  deleteAgentTelegram: (agentId: string) =>
    request<{ ok: boolean }>(`/agents/${agentId}/telegram`, { method: "DELETE" }),

  /* conversations */
  listConversations: (agentId?: string) =>
    request<Conversation[]>(`/conversations${agentId ? `?agent_id=${agentId}` : ""}`),
  getConversation: (id: string) => request<Conversation>(`/conversations/${id}`),
  deleteConversation: (id: string) =>
    request<{ ok: boolean }>(`/conversations/${id}`, { method: "DELETE" }),
  messages: (conversationId: string, afterSeq = 0) =>
    request<Message[]>(`/conversations/${conversationId}/messages?after_seq=${afterSeq}`),

  /* attachments */
  uploadAttachment: async (
    file: File,
  ): Promise<{ id: string; mime: string; size_bytes: number }> => {
    const form = new FormData()
    form.append("file", file)
    const response = await fetch(`/api/v1/attachments`, {
      method: "POST",
      // NOTE: no Content-Type here — the browser sets the multipart boundary itself.
      headers: { ...authHeaders() },
      body: form,
    })
    if (!response.ok) {
      // Same shape as request(): the backend returns {error: {code, message, details}};
      // fall back to a generic message only when the body isn't that shape.
      let code = "upload_failed"
      let message = "Upload failed"
      let details: unknown
      try {
        const body = await response.json()
        if (body?.error) {
          code = body.error.code ?? code
          message = body.error.message ?? message
          details = body.error.details
        }
      } catch {
        /* not JSON */
      }
      throw new ApiError(response.status, code, message, details)
    }
    return response.json()
  },
  attachmentBlobUrl: async (id: string): Promise<string> => {
    const response = await fetch(`/api/v1/attachments/${id}`, { headers: { ...authHeaders() } })
    if (!response.ok) throw new ApiError(response.status, "fetch_failed", "Could not load image")
    return URL.createObjectURL(await response.blob())
  },

  /* runs */
  listRuns: (
    params: {
      agentId?: string
      conversationId?: string
      status?: string
      trigger?: string
      telegramChatId?: number
    } = {},
  ) => {
    const query = new URLSearchParams()
    if (params.agentId) query.set("agent_id", params.agentId)
    if (params.conversationId) query.set("conversation_id", params.conversationId)
    if (params.status) query.set("status", params.status)
    if (params.trigger) query.set("trigger", params.trigger)
    if (params.telegramChatId !== undefined)
      query.set("telegram_chat_id", String(params.telegramChatId))
    const suffix = query.toString()
    return request<Run[]>(`/runs${suffix ? `?${suffix}` : ""}`)
  },
  listTelegramChats: () => request<TelegramChatRef[]>("/telegram-chats"),
  getRun: (id: string) => request<RunDetail>(`/runs/${id}`),
  runTrace: (id: string) => request<RunTrace>(`/runs/${id}/trace`),
  cancelRun: (id: string) => request<Run>(`/runs/${id}/cancel`, { method: "POST" }),

  /* schedules */
  listSchedules: (agentId?: string) =>
    request<Schedule[]>(`/schedules${agentId ? `?agent_id=${agentId}` : ""}`),
  createSchedule: (payload: Record<string, unknown>) =>
    request<Schedule>("/schedules", { method: "POST", body: JSON.stringify(payload) }),
  updateSchedule: (id: string, payload: Record<string, unknown>) =>
    request<Schedule>(`/schedules/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteSchedule: (id: string) =>
    request<{ ok: boolean }>(`/schedules/${id}`, { method: "DELETE" }),
  previewCron: (cronExpr: string, timezone: string) =>
    request<SchedulePreview>(
      `/schedules/preview?cron_expr=${encodeURIComponent(cronExpr)}&timezone=${encodeURIComponent(timezone)}`,
      { method: "POST" },
    ),
  runScheduleNow: (id: string) =>
    request<Run>(`/schedules/${id}/run-now`, { method: "POST" }),

  /* plugins */
  listPlugins: () => request<Plugin[]>("/plugins"),
  agentPlugins: (agentId: string) => request<AgentPlugin[]>(`/agents/${agentId}/plugins`),
  saveAgentPlugin: (agentId: string, slug: string, payload: Record<string, unknown>) =>
    request<AgentPlugin>(`/agents/${agentId}/plugins/${slug}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  deleteAgentPlugin: (agentId: string, slug: string) =>
    request<{ ok: boolean }>(`/agents/${agentId}/plugins/${slug}`, { method: "DELETE" }),
  startPluginOAuth: (agentId: string, slug: string) =>
    request<PluginOAuthStart>(`/agents/${agentId}/plugins/${slug}/oauth/start`, {
      method: "POST",
    }),
  completePluginOAuth: (payload: { code: string; state: string; code_verifier: string }) =>
    request<AgentPlugin>("/plugin-oauth/complete", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  disconnectPluginOAuth: (agentId: string, slug: string) =>
    request<{ ok: boolean }>(`/agents/${agentId}/plugins/${slug}/oauth`, { method: "DELETE" }),

  /* mail plugin */
  mailPresets: (agentId: string) =>
    request<MailPreset[]>(`/agents/${agentId}/plugins/mail/presets`),
  listMailAccounts: (agentId: string) =>
    request<MailAccount[]>(`/agents/${agentId}/plugins/mail/accounts`),
  createMailAccount: (agentId: string, payload: Record<string, unknown>) =>
    request<MailAccount>(`/agents/${agentId}/plugins/mail/accounts`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateMailAccount: (agentId: string, accountId: string, payload: Record<string, unknown>) =>
    request<MailAccount>(`/agents/${agentId}/plugins/mail/accounts/${accountId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  testMailAccount: (agentId: string, accountId: string) =>
    request<MailAccount>(`/agents/${agentId}/plugins/mail/accounts/${accountId}/test`, {
      method: "POST",
    }),
  deleteMailAccount: (agentId: string, accountId: string) =>
    request<{ ok: boolean }>(`/agents/${agentId}/plugins/mail/accounts/${accountId}`, {
      method: "DELETE",
    }),

  /* notion plugin */
  getNotionConnection: (agentId: string) =>
    request<NotionConnection | null>(`/agents/${agentId}/plugins/notion/connection`),
  saveNotionConnection: (agentId: string, token: string) =>
    request<NotionConnection>(`/agents/${agentId}/plugins/notion/connection`, {
      method: "PUT",
      body: JSON.stringify({ token }),
    }),
  testNotionConnection: (agentId: string) =>
    request<NotionConnection>(`/agents/${agentId}/plugins/notion/connection/test`, {
      method: "POST",
    }),
  deleteNotionConnection: (agentId: string) =>
    request<{ ok: boolean }>(`/agents/${agentId}/plugins/notion/connection`, {
      method: "DELETE",
    }),

  /* telegram plugin */
  getTelegramState: (agentId: string) =>
    request<TelegramPanelState>(`/agents/${agentId}/plugins/telegram/connection`),
  startTelegramLogin: (agentId: string) =>
    request<TelegramLoginStart>(`/agents/${agentId}/plugins/telegram/login/start`, {
      method: "POST",
    }),
  telegramLoginStatus: (agentId: string, loginId: string) =>
    request<TelegramLoginStatus>(`/agents/${agentId}/plugins/telegram/login/${loginId}`),
  submitTelegramPassword: (agentId: string, loginId: string, password: string) =>
    request<{ ok: boolean }>(`/agents/${agentId}/plugins/telegram/login/${loginId}/password`, {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
  cancelTelegramLogin: (agentId: string, loginId: string) =>
    request<{ ok: boolean }>(`/agents/${agentId}/plugins/telegram/login/${loginId}`, {
      method: "DELETE",
    }),
  testTelegramConnection: (agentId: string) =>
    request<TelegramConnection>(`/agents/${agentId}/plugins/telegram/connection/test`, {
      method: "POST",
    }),
  deleteTelegramConnection: (agentId: string) =>
    request<{ ok: boolean }>(`/agents/${agentId}/plugins/telegram/connection`, {
      method: "DELETE",
    }),

  /* memories */
  listMemories: (agentId: string) => request<Memory[]>(`/agents/${agentId}/memories`),
  createMemory: (agentId: string, content: string) =>
    request<Memory>(`/agents/${agentId}/memories`, {
      method: "POST",
      body: JSON.stringify({ content }),
    }),
  updateMemory: (agentId: string, memoryId: string, content: string) =>
    request<Memory>(`/agents/${agentId}/memories/${memoryId}`, {
      method: "PATCH",
      body: JSON.stringify({ content }),
    }),
  deleteMemory: (agentId: string, memoryId: string) =>
    request<{ ok: boolean }>(`/agents/${agentId}/memories/${memoryId}`, {
      method: "DELETE",
    }),
}

/** Resolves and persists the active workspace if none is set yet. Called
 *  fire-and-forget at app bootstrap (auth.tsx) - never awaited there, since
 *  blocking app load on this fetch would let a hung request wedge the whole
 *  app on the loading screen forever. Safe to not block on: until this
 *  resolves, requests simply omit X-Workspace-Id and the backend already
 *  defaults to the caller's default workspace - the same one this picks -
 *  so there's no correctness gap, only a brief window before the switcher
 *  has a persisted choice to display. Idempotent (a set workspace id makes
 *  this a no-op), so calling it again elsewhere (e.g. defensively from the
 *  switcher) is cheap and safe. The fetch is capped at 8s via AbortController
 *  so a hung backend can't leak the request forever; any abort/error is
 *  swallowed the same as an ordinary failure. */
export async function ensureActiveWorkspace(): Promise<void> {
  if (getActiveWorkspaceId()) return
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 8000)
  try {
    const workspaces = await request<Workspace[]>("/workspaces", { signal: controller.signal })
    const target = workspaces.find((workspace) => workspace.is_default) ?? workspaces[0]
    if (target) setActiveWorkspaceId(target.id)
  } catch {
    /* best effort - see doc comment above (includes an abort on timeout) */
  } finally {
    clearTimeout(timer)
  }
}

/* ------------------------------------------------------------------ streaming */

export interface StreamHandle {
  runId: string | null
  cancel: () => void
  done: Promise<void>
}

/**
 * Start a run and consume its event stream.
 *
 * `fetch` rather than `EventSource`, because EventSource cannot send an
 * Authorization header. Errors before the stream opens are real HTTP responses, so
 * they surface as a thrown ApiError rather than as a stream event.
 */
export async function streamRun(
  path: string,
  body: Record<string, unknown>,
  handlers: {
    onEvent: (event: RunEvent) => void
    onError?: (error: Error) => void
    onRunId?: (runId: string) => void
  },
): Promise<StreamHandle> {
  const controller = new AbortController()

  const response = await fetch(`/api/v1${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...authHeaders(),
    },
    body: JSON.stringify(body),
    signal: controller.signal,
  })

  if (!response.ok) {
    let code = "http_error"
    let message = `Request failed with status ${response.status}`
    try {
      const payload = await response.json()
      code = payload?.error?.code ?? code
      message = payload?.error?.message ?? message
    } catch {
      /* not JSON */
    }
    throw new ApiError(response.status, code, message)
  }

  const runId = response.headers.get("X-Run-Id")
  if (runId) handlers.onRunId?.(runId)

  const done = (async () => {
    const reader = response.body?.getReader()
    if (!reader) return
    const decoder = new TextDecoder()
    let buffer = ""

    try {
      for (;;) {
        const { done: finished, value } = await reader.read()
        if (finished) break
        buffer += decoder.decode(value, { stream: true })

        const { frames, rest } = splitFrames(buffer)
        buffer = rest
        for (const frame of frames) {
          const event = parseFrame(frame)
          if (event) handlers.onEvent(event)
        }
      }
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        handlers.onError?.(error as Error)
      }
    }
  })()

  return { runId, cancel: () => controller.abort(), done }
}
